import React from "react";

import {
  PRIMARY_RESIDENCE,
  SECOND_HOME
} from "../../../constants/liability_types";
import { JUMBO } from "../../../constants/loan_types";
import OCCUPANCY from "../../../constants/occupancy";

import PercentField from "../../fields/percent_field";
import TextField from "../../fields/text_field";
import CurrencyField from "../../fields/currency_field";
import NumberField from "../../fields/number_field";

import {
  total_payment,
  wrap_investment_properties,
  wrap_subject_property_if_needed
} from "../../../calculations/investment_reo";
import {
  jumbo_reserves_required_total,
  total_cost_to_purchase
} from "../../../calculations/loan";
import {
  reserve_total_payment,
  residence_total_payment
} from "../../../calculations/debt_info";
import {
  calculations_percentage,
  monthly_percent_reserves,
  reo_reserve_months,
  reserves_positive_or_shortage,
  subject_property_monthly_percent_reserves,
  subject_property_monthly_pitia,
  subject_property_monthly_reserves,
  subject_property_outstanding_upb,
  subject_property_reserve_months,
  subject_property_total_reserves,
  total_reserve_requirement_for_loan_type,
  total_reserves_available,
  reserve_reo_properties,
  residence_duplicates_reo
} from "../../../calculations/reserve";
import { summary_selected_loan } from "../../../constants/utils";
import { PRIMARY, SECONDARY } from "../../../constants/property_disposition";

import { useDocument } from "../../loan_tools/loan_tool";
import Section from "../../ui/section";
import { PURCHASE } from "../../../constants/document_types";

export default function TotalReserveCalculator() {
  const [document, setDocument, { documentType }] = useDocument();
  const {
    applicant,
    accountSummaries,
    loans,
    property,
    investment_properties,
    residences,
    property_to_be_sold,
    is_selling_property
  } = document;
  const borrowers = applicant.borrowers;
  // Include/exclude picker: unchecking a property drops it from the reserve
  // requirement (all loans) but leaves the row visible + everything else intact.
  const excluded = document.reserves_excluded || [];
  const isExcluded = key => excluded.includes(key);
  function toggleExcluded(key) {
    const next = excluded.includes(key)
      ? excluded.filter(k => k !== key)
      : [...excluded, key];
    setDocument({ ...document, reserves_excluded: next });
  }
  function includeCell(key) {
    return (
      <td className="reserve-include-cell">
        <input
          type="checkbox"
          checked={!isExcluded(key)}
          onChange={() => toggleExcluded(key)}
          aria-label="Include in reserve requirement"
        />
      </td>
    );
  }
  const rowClass = key => (isExcluded(key) ? "reserve-row-excluded" : "");
  // The current primary residence gets no reserve line here (an owner-occupied
  // home the borrower keeps needs no reserves; a retained-investment home flows
  // in through the Invest REO list instead). Only 2nd homes remain as residence
  // rows.
  // A residence that is the same home as an Invest REO row is shown (and
  // counted) once, on its Invest REO row.
  const residenceAccounts = Object.values(accountSummaries).filter(
    accountSummary =>
      filterResidenceAccountSummaries(accountSummary) &&
      accountSummary.liability_type !== PRIMARY_RESIDENCE &&
      residences[accountSummary.residence] &&
      !residence_duplicates_reo(
        residences[accountSummary.residence],
        investment_properties
      )
  );
  const isPurchase = documentType === PURCHASE;

  const wrapped_investment_properties = wrap_investment_properties(
      property, investment_properties, false);
  // The Subject row + "Total of Reserves" footer follow the Summary Sheet's
  // selected loan; before one is selected, fall back to Loan 1 (otherwise the
  // footer showed $0 while the per-loan Total Reserve Requirement rows below
  // showed the real amount).
  const calculatorLoan =
    summary_selected_loan(loans, document.summary) || (loans || [])[0];

  function renderNewPropertyRow() {
    const {
      property,
      investment_properties,
      residences
    } = document;
    const loan = calculatorLoan;
    const wrapped_investment_properties = wrap_investment_properties(
        property, investment_properties, false);
    return (
      <tr className={rowClass("subject")}>
        {includeCell("subject")}
        <td>
          <TextField readOnly value="Subject Property" />
        </td>
        <td>
          <TextField readOnly value={OCCUPANCY[property.occupancy]} />
        </td>
        <td>
          <CurrencyField
            readOnly
            value={subject_property_outstanding_upb(
              documentType,
              loan,
              property,
              property_to_be_sold,
              is_selling_property
            )}
          />
        </td>
        <td>
          <CurrencyField
            readOnly
            value={subject_property_monthly_pitia(
              documentType,
              loan,
              property,
              property_to_be_sold,
              is_selling_property
            )}
          />
        </td>
        <td>
          <NumberField
            readOnly
            value={subject_property_reserve_months(property)}
          />
        </td>
        <td>
          <PercentField
            readOnly
            value={calculations_percentage(wrapped_investment_properties, residences)}
          />
        </td>
        <td>
          <CurrencyField
            readOnly
            value={subject_property_monthly_reserves(
              documentType,
              loan,
              property,
              property_to_be_sold,
              is_selling_property
            )}
          />
        </td>
        <td>
          <CurrencyField
            readOnly
            value={subject_property_monthly_percent_reserves(
              documentType,
              loan,
              property,
              wrapped_investment_properties,
              residences,
              property_to_be_sold,
              is_selling_property
            )}
          />
        </td>
        <td>
          <CurrencyField
            readOnly
            value={subject_property_total_reserves(
              documentType,
              loan,
              property,
              wrapped_investment_properties,
              residences,
              property_to_be_sold,
              is_selling_property
            )}
          />
        </td>
      </tr>
    );
  }

  function renderInvesmentREORow(investmentREO, index) {
    const { investment_properties, residences, property } = document;
    const wrapped_investment_properties = wrap_investment_properties(
        property, investment_properties);
    const wrappedREO = wrap_subject_property_if_needed(property, investmentREO);
    const monthlyPITIA = total_payment(wrappedREO);
    // Tier reserves by the row's Intended Occupancy (agency rule), matching the
    // total_reserve_requirement calc: Primary = 0 months (Owner), Second Home = 2
    // (2nd Home), Investment = 6 (Non-Owner).
    const reserveMonths = reo_reserve_months(wrappedREO);
    const occupancyLabel =
      wrappedREO.intended_occupancy === PRIMARY
        ? "Owner"
        : wrappedREO.intended_occupancy === SECONDARY
        ? "2nd Home"
        : "Non-Owner";
    const isZero = reserveMonths === 0;
    const reservePercentage = isZero
      ? "0"
      : calculations_percentage(wrapped_investment_properties, residences);
    const monthlyReserveAmount = isZero
      ? "0"
      : reserve_total_payment(reserveMonths, monthlyPITIA);
    const monthlyPercentReserves = isZero
      ? "0"
      : monthly_percent_reserves(
          wrappedREO,
          wrapped_investment_properties,
          residences
        );
    const totalReserves = isZero
      ? "0"
      : monthlyPercentReserves.plus(monthlyReserveAmount);

    const key = "reo:" + wrappedREO.property_address;
    return (
      <tr key={index} className={rowClass(key)}>
        {includeCell(key)}
        <td>
          <TextField readOnly value={wrappedREO.property_address} />
        </td>
        <td>
          <TextField readOnly value={occupancyLabel} />
        </td>
        <td>
          <CurrencyField readOnly value={wrappedREO.principal_balance} />
        </td>
        <td>
          <CurrencyField readOnly value={monthlyPITIA} />
        </td>
        <td>
          <NumberField readOnly value={reserveMonths} />
        </td>
        <td>
          <PercentField readOnly value={reservePercentage} />
        </td>
        <td>
          <CurrencyField readOnly value={monthlyReserveAmount} />
        </td>
        <td>
          <CurrencyField readOnly value={monthlyPercentReserves} />
        </td>
        <td>
          <CurrencyField readOnly value={totalReserves} />
        </td>
      </tr>
    );
  }

  function renderResidenceRow(accountSummary, index) {
    const residence = document.residences[accountSummary.residence];
    let propType,
      address,
      reserveMonths,
      reservePercentage = calculations_percentage(
        wrapped_investment_properties,
        residences
      ),
      monthlyPercentReserves,
      monthlyReserveAmount,
      totalReserves;

    const residenceTotalPayment = residence_total_payment(
      residence.first_mortgage,
      residence.second_mortgage,
      residence.taxes,
      residence.insurance,
      residence.hoa
    );

    if (accountSummary.liability_type === PRIMARY_RESIDENCE) {
      address = `Primary Residence`;
      propType = "Owner";
      reserveMonths = "0";
      reservePercentage = "0";
      monthlyReserveAmount = "0";
      monthlyPercentReserves = "0";
      totalReserves = "0";
    } else {
      address = residence.address;
      propType = "2nd Home";
      reserveMonths = "2";
      monthlyReserveAmount = reserve_total_payment(
        reserveMonths,
        residenceTotalPayment
      );
      monthlyPercentReserves = monthly_percent_reserves(
        residence,
        wrapped_investment_properties,
        residences
      );
      totalReserves = monthlyPercentReserves.plus(monthlyReserveAmount);
    }

    const key = "residence:" + accountSummary.id;
    return (
      <tr key={index} className={rowClass(key)}>
        {includeCell(key)}
        <td>
          <TextField readOnly value={address} />
        </td>
        <td>
          <TextField readOnly value={propType} />
        </td>
        <td>
          <CurrencyField readOnly value={residence.principal_balance} />
        </td>
        <td>
          <CurrencyField readOnly value={residenceTotalPayment} />
        </td>
        <td>
          <NumberField readOnly value={reserveMonths} />
        </td>
        <td>
          {reservePercentage === "0" ? (
            <TextField readOnly value="N/A" />
          ) : (
            <PercentField readOnly value={reservePercentage} />
          )}
        </td>
        <td>
          <CurrencyField readOnly value={monthlyReserveAmount} />
        </td>
        <td>
          <CurrencyField readOnly value={monthlyPercentReserves} />
        </td>
        <td>
          <CurrencyField readOnly value={totalReserves} />
        </td>
      </tr>
    );
  }

  function renderLoanJumboReserves(loan, loanIndex) {
    if (loan.type === JUMBO) {
      return (
        <div key={loanIndex}>
          <NumberField
            name={`Jumbo Reserves required (Loan ${loanIndex + 1})`}
            readOnly
            value={jumbo_reserves_required_total(loan)}
          />
        </div>
      );
    } else {
      return <div key={loanIndex} />;
    }
  }

  function filterResidenceAccountSummaries(accountSummary) {
    const residenceLiabilityTypes = [PRIMARY_RESIDENCE, SECOND_HOME];
    return residenceLiabilityTypes.includes(accountSummary.liability_type);
  }

  return (
    <>
      <Section
        className="total-reserve-calculator"
        name="Total Reserve Calculator"
      >
        <table className="reserve-property-table">
          <thead>
            <tr>
              <th className="reserve-include-col">Include</th>
              <th>Occupancy</th>
              <th>Prop Type</th>
              <th>Outstanding UPB</th>
              <th>Monthly PITIA</th>
              <th>Res. Mos.</th>
              <th>Calc %</th>
              <th>PITI Reserves</th>
              <th>UPB % Reserves</th>
              <th>Total Reserves</th>
            </tr>
          </thead>
          <tbody>
            {renderNewPropertyRow()}
            {residenceAccounts.map(renderResidenceRow)}
            {/* Each property once: duplicates of the same address (e.g. the
                kept home imported as an owner row) are dropped automatically. */}
            {reserve_reo_properties(wrapped_investment_properties).map(
              renderInvesmentREORow
            )}
          </tbody>
          <tfoot>
            <tr>
              <td />
              <td className="bold">Total of Reserves</td>
              <td />
              <td />
              <td />
              <td />
              <td />
              <td />
              <td />
              <td>
                <CurrencyField
                  readOnly
                  value={total_reserve_requirement_for_loan_type(
                    documentType,
                    loans,
                    calculatorLoan,
                    property,
                    wrapped_investment_properties,
                    residences,
                    accountSummaries,
                    property_to_be_sold,
                    is_selling_property,
                    borrowers,
                    excluded
                  )}
                />
              </td>
            </tr>
          </tfoot>
        </table>
        <div />

        <div>{loans.map(renderLoanJumboReserves)}</div>

        <table className="total-reserve-calculator-bottom-table">
          <tbody>
            <tr>
              <th scope="col">Reserve Summary For Loans</th>
              {loans.map((loan, index) => (
                <td key={index} className="bold center-align">
                  Loan {index + 1}
                </td>
              ))}
            </tr>
            {isPurchase && (
              <tr>
                <th scope="col">Total Cost To Purchase Home</th>
                {loans.map((loan, index) => (
                  <td key={index}>
                    <CurrencyField
                      readOnly
                      value={total_cost_to_purchase(
                        documentType,
                        loan,
                        property,
                        property_to_be_sold,
                        is_selling_property
                      )}
                    />
                  </td>
                ))}
              </tr>
            )}
            <tr>
              <th scope="col">Total Reserves Available</th>
              {loans.map((loan, index) => (
                <td key={index}>
                  <CurrencyField
                    readOnly
                    value={total_reserves_available(
                      documentType,
                      borrowers,
                      accountSummaries,
                      residences,
                      property_to_be_sold,
                      loan,
                      is_selling_property
                    )}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <th scope="col">Total Reserve Requirement</th>
              {loans.map((loan, index) => (
                <td key={index}>
                  <CurrencyField
                    readOnly
                    value={total_reserve_requirement_for_loan_type(
                      documentType,
                      loans,
                      loan,
                      property,
                      wrapped_investment_properties,
                      residences,
                      accountSummaries,
                      property_to_be_sold,
                      is_selling_property,
                      borrowers,
                      excluded
                    )}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <th scope="col">Reserves (Positive or Shortage)</th>
              {loans.map((loan, index) => (
                <td key={index}>
                  <CurrencyField
                    readOnly
                    value={reserves_positive_or_shortage(
                      documentType,
                      loans,
                      borrowers,
                      accountSummaries,
                      residences,
                      property_to_be_sold,
                      loan,
                      property,
                      is_selling_property,
                      wrapped_investment_properties,
                      excluded
                    )}
                  />
                </td>
              ))}
            </tr>
            {isPurchase && (
              <tr>
                <th scope="col">(After Purchase)</th>
              </tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section
        className="fnma-reserves-calculation"
        name="FNMA Reserves Calculation"
      >
        <table>
          <tbody>
            <tr>
              <td>FNMA Reserves Calculation</td>
              <td />
            </tr>
            <tr>
              <td>If Subject Property is:</td>
              <td>Then Reserves required for subject transaction is</td>
            </tr>
            <tr>
              <td>Primary Residence</td>
              <td>None</td>
            </tr>
            <tr>
              <td>2nd Home</td>
              <td>2 months PITIA</td>
            </tr>
            <tr>
              <td>Non-Owner Rental</td>
              <td>6 months PITIA</td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <td colSpan="2">
                Plus additional reserves for other properties owned based upon
                their respected Unpaid Principal Balance (UPB)**
              </td>
            </tr>
            <tr>
              <td>If 1-4 Financed Properties</td>
              <td>2% of the unpaid principal balance</td>
            </tr>
            <tr>
              <td>If 5-6 Financed Properties</td>
              <td>4% of the unpaid principal balance</td>
            </tr>
            <tr>
              <td>If 7-10 Financed Properties</td>
              <td>6% of the unpaid principal balance</td>
            </tr>
          </tbody>
        </table>
      </Section>
    </>
  );
}
